import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetCompatibleTemplateListByCountry } from "../../../../../../api/services/programTemplate/programTemplateApis";
import {
  FormController,
  MuiBox,
  MuiButtonSecondary,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../../../../components";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import {
  CompatibleTemplateByCountry,
  ProgramTemplateAddCompatibleFormKey,
} from "../../../../modelsAndSchemas/Models";
import { createProgramSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { MODULE_PERMISSIONS } from "../../../../../../helpers/constants";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";

interface CompatibleTemplateProps {
  pageId: string;
  countryList: OptionsDataType[];
  disabledFields: boolean;
  templateId: string;
  handleAddCompatibleTemplate: () => void;
}

export const AddCompatibleTemplate: FC<CompatibleTemplateProps> = ({
  pageId,
  countryList,
  disabledFields,
  templateId,
  handleAddCompatibleTemplate,
}) => {
  const { getValues, watch, control, setValue } = useFormContext();
  const dispatch = useDispatch();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const {
    refetchWithParams: getCompatibleTemplateListByCountry,
    data: compatibleTemplateListByCountry,
    isLoading: isListByCountryLoading,
    isError: isListByCountryError,
  } = useGetCompatibleTemplateListByCountry();

  const getLabel = (key: string) => {
    return fetchLabel(createProgramSiteCoreKeys.compatibleTemplateKey, key);
  };

  useEffect(() => {
    const { country } = getValues();
    if (isNotEmpty(country)) {
      getCompatibleTemplateListByCountry({
        templateId,
        countryId: country,
      });
      setValue(
        ProgramTemplateAddCompatibleFormKey.compatibleProgramTemplate,
        "",
      );
      setValue(ProgramTemplateAddCompatibleFormKey.duration, "");
    }
  }, [watch(ProgramTemplateAddCompatibleFormKey.country)]);

  useEffect(() => {
    const { compatibleProgramTemplate } = getValues();
    if (
      isNotEmpty(compatibleProgramTemplate) &&
      compatibleTemplateListByCountry
    ) {
      compatibleTemplateListByCountry.forEach(
        (template: CompatibleTemplateByCountry) => {
          if (template.id === compatibleProgramTemplate) {
            setValue(
              ProgramTemplateAddCompatibleFormKey.duration,
              template.durationInMonths,
            );
          }
        },
      );
    }
  }, [watch(ProgramTemplateAddCompatibleFormKey.compatibleProgramTemplate)]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isListByCountryLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isListByCountryLoading]);

  useEffect(() => {
    if (isListByCountryError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isListByCountryError]);

  return (
    <>
      <MuiTypography
        variant="h3Bold"
        className="template-header"
        component="div"
        data-testid="add-compatible-template"
      >
        {getLabel(createProgramSiteCoreKeys.addTemplateSubTitle)}
      </MuiTypography>
      <MuiGrid container className="form-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProgramTemplateAddCompatibleFormKey.country}
          >
            <MuiSelect
              label={getLabel(createProgramSiteCoreKeys.compatibleCountry)}
              options={countryList}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ProgramTemplateAddCompatibleFormKey.country}`}
              isMandatory={!disabledFields}
              placeholder={commonLabels.selectPlaceHolder}
              showSearch
              disabled={disabledFields}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProgramTemplateAddCompatibleFormKey.compatibleProgramTemplate}
          >
            <MuiSelect
              label={getLabel(
                createProgramSiteCoreKeys.compatibleProgramTemplate,
              )}
              options={
                getValues().country
                  ? setApiDataToDropDownOption(compatibleTemplateListByCountry)
                  : []
              }
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ProgramTemplateAddCompatibleFormKey.compatibleProgramTemplate}`}
              isMandatory={!disabledFields}
              placeholder={commonLabels.selectPlaceHolder}
              showSearch
              disabled={disabledFields}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProgramTemplateAddCompatibleFormKey.duration}
          >
            <MuiTextField
              label={getLabel(createProgramSiteCoreKeys.duration)}
              id={`${pageId}-${ProgramTemplateAddCompatibleFormKey.duration}`}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiBox className="add-button-container">
        <PermissionWrapper
          permission={MODULE_PERMISSIONS.ADD_COMPATIBLE_TEMPLATES}
        >
          <MuiButtonSecondary
            disabled={disabledFields}
            onClick={handleAddCompatibleTemplate}
            id={`${pageId}-add-compatible-template`}
          >
            {commonLabels.add}
          </MuiButtonSecondary>
        </PermissionWrapper>
      </MuiBox>
    </>
  );
};
